import { useEffect, useRef, useState } from "react";
import { Link2, Loader2, CheckCircle2, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import PrecheckCard from "../app/PrecheckCard";
import { MODELS } from "./constants";
import { StepNav } from "./Stepper";

const DRAFT = "gi_studio_draft";
const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT) || "{}"); } catch { return {}; }
};

function ReadOnlyJob({ app, onNew, onBack, onNext }) {
  return (
    <div className="af-fade-up" data-testid="studio-step-job-readonly">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">The job</h1>
      <p className="text-body mt-2 mb-6">{app.role || "Role"} at {app.company || "the company"}</p>
      <div className="gi-card p-5">
        {app.jd_url && <a href={app.jd_url} target="_blank" rel="noreferrer" className="text-sm text-brand inline-flex items-center gap-1.5 mb-3 hover:underline"><ExternalLink className="w-4 h-4" /> Open the original job post</a>}
        <div className="max-h-80 overflow-y-auto rounded-xl bg-canvas border border-line p-4 text-[15px] text-body whitespace-pre-wrap leading-relaxed">{app.jd_text}</div>
      </div>
      <StepNav onBack={onBack} onNext={onNext} nextLabel="See your compatibility"
        extra={<button onClick={onNew} data-testid="studio-new-from-job" className="gi-btn gi-btn-tertiary"><Plus className="w-4 h-4" /> Different job</button>} />
    </div>
  );
}

export default function StepJob({ app, onBack, onStart, onNew, onNext }) {
  const d = useRef(loadDraft()).current;
  const [url, setUrl] = useState(() => localStorage.getItem("pending_url") || d.url || "");
  const [jd, setJd] = useState(() => localStorage.getItem("pending_jd") || d.jd || "");
  const [company, setCompany] = useState(d.company || "");
  const [model, setModel] = useState(d.model || "claude");
  const [fetching, setFetching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [askOverride, setAskOverride] = useState(false);
  const [saved, setSaved] = useState(false);
  const checkedFor = useRef("");

  useEffect(() => {
    if (localStorage.getItem("pending_jd") || localStorage.getItem("pending_url")) {
      localStorage.removeItem("pending_jd");
      localStorage.removeItem("pending_url");
      toast.success("We've added the job you pasted earlier.");
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem(DRAFT, JSON.stringify({ url, jd, company, model }));
      setSaved(Boolean(jd || url));
    }, 600);
    return () => clearTimeout(t);
  }, [url, jd, company, model]);

  const runPrecheck = async (text) => {
    setChecking(true);
    try {
      const { data } = await api.post("/jobs/precheck", { jd_text: text, company });
      checkedFor.current = text;
      setPrecheck(data);
      if (data.company) setCompany((c) => c || data.company);
      return data;
    } catch {
      setPrecheck(null);
      return null;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    setPrecheck(null);
    setAskOverride(false);
    const text = jd.trim();
    if (text.length < 150) return;
    const t = setTimeout(() => runPrecheck(text), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jd]);

  if (app) return <ReadOnlyJob app={app} onNew={onNew} onBack={onBack} onNext={onNext} />;

  const fetchAd = async () => {
    if (!/^https?:\/\//i.test(url.trim())) return toast.error("Paste a full link starting with https://");
    setFetching(true);
    try {
      const { data } = await api.post("/scrape", { url: url.trim() });
      setJd(data.jd_text || "");
      setCompany(data.company || "");
      toast.success("Got it. Check the job ad below looks right.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That site wouldn't let us read the ad. Copy and paste the text instead.");
    } finally {
      setFetching(false);
    }
  };

  const start = async (override = false) => {
    if (jd.trim().length < 40) return toast.error("Paste the full job ad first (at least a few sentences).");
    let check = precheck;
    if (override !== true && checkedFor.current !== jd.trim()) check = await runPrecheck(jd.trim());
    if (check?.flagged && override !== true) {
      setAskOverride(true);
      return toast.warning("This job post has warning signs. Please read the note above first.");
    }
    setStarting(true);
    try {
      const { data } = await api.post("/applications/tailor", { jd_text: jd, jd_url: url, model });
      localStorage.removeItem(DRAFT);
      onStart(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't start just now. Your job ad is saved, so please try again.");
      setStarting(false);
    }
  };

  const short = jd.trim().length < 40;
  return (
    <div className="af-fade-up" data-testid="studio-step-job">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Which job are you applying for?</h1>
      <p className="text-body mt-2 mb-6 max-w-2xl">Paste the job ad. We'll check the post looks genuine, show how well you fit, and tailor your real experience to it.</p>

      <div className="gi-card p-5 sm:p-6 space-y-5">
        <div>
          <label htmlFor="job-link" className="gi-label">Job link <span className="font-normal text-faint">(optional)</span></label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 text-faint absolute left-3.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input id="job-link" data-testid="job-link-input" value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url"
                placeholder="https://company.com/careers/role" className="gi-input pl-10" />
            </div>
            <button data-testid="scrape-job-button" onClick={fetchAd} disabled={fetching || !url.trim()} className="gi-btn gi-btn-secondary">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {fetching ? "Reading the page…" : "Get the job ad"}
            </button>
          </div>
          <p className="text-sm text-faint mt-1.5">Some sites (like LinkedIn) block this. If so, copy the ad text into the box below.</p>
        </div>
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="job-ad" className="gi-label">Job ad</label>
            {saved && <span className="text-xs text-faint flex items-center gap-1" data-testid="draft-saved"><CheckCircle2 className="w-3.5 h-3.5 text-success" /> Draft saved</span>}
          </div>
          <textarea id="job-ad" data-testid="jd-text-area" value={jd} onChange={(e) => setJd(e.target.value)} rows={9}
            placeholder="Paste the whole job ad here: what you'd do, what they're looking for, skills and requirements."
            className="gi-input leading-relaxed resize-y" />
        </div>
      </div>

      <div className="mt-4"><PrecheckCard checking={checking} data={precheck} askOverride={askOverride} onOverride={() => start(true)} jdText={jd.trim()} company={company} onCompanyChange={setCompany} onReported={() => setPrecheck((p) => ({ ...p, you_reported: true }))} /></div>

      <fieldset className="gi-card p-5 sm:p-6 mt-4">
        <legend className="sr-only">Choose the AI</legend>
        <div className="gi-label">Which AI should write it?</div>
        <p className="text-sm text-faint mb-3">All of them follow the same honesty rules. Pick the one you like; you can change it next time.</p>
        <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-2.5" role="radiogroup">
          {MODELS.map((m) => (
            <button key={m.id} role="radio" aria-checked={model === m.id} data-testid={`model-${m.id}`} onClick={() => setModel(m.id)}
              className={`text-left px-4 py-3 rounded-xl border min-h-[64px] transition-colors ${model === m.id ? "border-brand bg-brand-soft ring-1 ring-brand" : "border-line bg-surface hover:border-brand/40"}`}>
              <div className="text-[15px] font-semibold text-ink flex items-center gap-1.5">
                {model === m.id && <CheckCircle2 className="w-4 h-4 text-brand" data-testid={`model-${m.id}-selected`} />} {m.label}
              </div>
              <div className="text-sm text-body">{m.hint}</div>
            </button>
          ))}
        </div>
      </fieldset>

      <StepNav onBack={onBack} onNext={() => start()} busy={starting || checking} nextDisabled={short}
        nextLabel={starting ? "Starting…" : checking ? "Checking the job post…" : "Check my fit & tailor my CV"} />
      {short && <p className="text-sm text-faint mt-2 text-right" data-testid="jd-hint">Paste the job ad to continue.</p>}
    </div>
  );
}
